<script lang="ts" setup>
import { onMounted, ref, reactive, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import PanelGrid from '@/views/shared/components/PanelGrid.vue';
import PanelCard from '@/views/shared/components/PanelCard.vue';
import PageHeader from '@/components/PageHeader.vue';
import { emailService } from '@/services/email.service';
import { useRecentlyViewed } from '@/composables/useRecentlyViewed';
import { useSessionStore } from '@/stores/session-store';
import type {
  EmailTemplate,
  TemplateVariable,
  CreateEmailTemplateRequest,
  UpdateEmailTemplateRequest,
  PreviewEmailResult,
} from '@/services/email.service';

// System URL metadata for UI helper
interface SystemUrlInfo {
  name: string;
  templateVariable: string;
  description: string;
  requiredDataVariables: string[];
  pattern: string;
  category: 'Authentication' | 'Invitations' | 'Navigation';
}

const systemUrls: SystemUrlInfo[] = [
  {
    name: 'Accept Invitation',
    templateVariable: 'acceptInvitationUrl',
    description: 'URL for accepting organization invitations',
    requiredDataVariables: ['invitationToken'],
    pattern: '/invitations/accept?token={invitationToken}',
    category: 'Invitations',
  },
  {
    name: 'Accept Partnership',
    templateVariable: 'acceptPartnershipUrl',
    description: 'URL for accepting organization partnership invitations',
    requiredDataVariables: ['partnershipToken'],
    pattern: '/invitations/partnership/accept?token={invitationToken}',
    category: 'Invitations',
  },
  {
    name: 'Reset Password',
    templateVariable: 'resetPasswordUrl',
    description: 'URL for password reset',
    requiredDataVariables: ['resetCode'],
    pattern: '/auth/reset-password?code={resetCode}',
    category: 'Authentication',
  },
  {
    name: 'Verify Email',
    templateVariable: 'verifyEmailUrl',
    description: 'URL for email verification',
    requiredDataVariables: ['verificationCode'],
    pattern: '/auth/verify-email?code={verificationCode}',
    category: 'Authentication',
  },
  {
    name: 'View Project',
    templateVariable: 'viewProjectUrl',
    description: 'URL for viewing a specific project',
    requiredDataVariables: ['projectId'],
    pattern: '/projects/{projectId}',
    category: 'Navigation',
  },
  {
    name: 'View Workflow',
    templateVariable: 'viewWorkflowUrl',
    description: 'URL for viewing a specific workflow within a project',
    requiredDataVariables: ['projectId', 'workflowId'],
    pattern: '/projects/{projectId}/workflows/{workflowId}',
    category: 'Navigation',
  },
  {
    name: 'View Workflow Run',
    templateVariable: 'viewWorkflowRunUrl',
    description: 'URL for viewing a workflow run',
    requiredDataVariables: ['projectId', 'workflowId', 'runId'],
    pattern: '/projects/{projectId}/workflows/{workflowId}/runs/{runId}',
    category: 'Navigation',
  },
  {
    name: 'View Product',
    templateVariable: 'viewProductUrl',
    description: 'URL for viewing a product',
    requiredDataVariables: ['productId'],
    pattern: '/products/{productId}',
    category: 'Navigation',
  },
  {
    name: 'Organization Settings',
    templateVariable: 'organizationSettingsUrl',
    description: 'URL for viewing organization settings',
    requiredDataVariables: ['orgId'],
    pattern: '/organizations/{orgId}/settings',
    category: 'Navigation',
  },
  {
    name: 'User Profile',
    templateVariable: 'userProfileUrl',
    description: 'URL for viewing user profile',
    requiredDataVariables: [],
    pattern: '/profile',
    category: 'Navigation',
  },
  {
    name: 'Dashboard',
    templateVariable: 'dashboardUrl',
    description: 'URL for the main dashboard',
    requiredDataVariables: [],
    pattern: '/dashboard',
    category: 'Navigation',
  },
  {
    name: 'App Base URL',
    templateVariable: 'appBaseUrl',
    description: 'Base URL of the application (e.g., https://localhost:5000)',
    requiredDataVariables: [],
    pattern: '',
    category: 'Navigation',
  },
];

const router = useRouter();
const route = useRoute();
const { trackView } = useRecentlyViewed();
const sessionStore = useSessionStore();

const templateId = computed(() => route.params.id as string);
const isNewTemplate = computed(() => route.params.id === 'new');

// State
const template = ref<EmailTemplate | null>(null);
const isLoading = ref(false);
const error = ref('');
const originalFormState = ref<string>('');
const activeTab = ref<'edit' | 'preview' | 'test'>('edit');
const isSaving = ref(false);
const saveResult = ref<{ success: boolean; message: string } | null>(null);

// Form state
const form = reactive({
  templateType: '',
  name: '',
  description: '',
  subject: '',
  htmlBody: '',
  textBody: '',
  variables: [] as TemplateVariable[],
  isActive: true,
});

// Preview state
const isLoadingPreview = ref(false);
const preview = ref<PreviewEmailResult | null>(null);
const previewMode = ref<'rendered' | 'html' | 'text'>('rendered');

// Shared variable values for both preview and test (synced between tabs)
const variableValues = reactive<Record<string, any>>({});

// Test email state
const isSendingTest = ref(false);
const testEmail = reactive({
  recipientEmail: sessionStore.session.user?.email || '',
});
const testResult = ref<{ success: boolean; message: string } | null>(null);

// System URL Helper Modal
const showSystemUrlModal = ref(false);
const systemUrlFilter = ref<'All' | 'Authentication' | 'Invitations' | 'Navigation'>('All');
const htmlBodyTextarea = ref<HTMLTextAreaElement | null>(null);
const textBodyTextarea = ref<HTMLTextAreaElement | null>(null);

// Computed
const isDirty = computed(() => {
  if (!originalFormState.value) return false;
  const currentState = JSON.stringify(form);
  return currentState !== originalFormState.value;
});

const filteredSystemUrls = computed(() => {
  if (systemUrlFilter.value === 'All') {
    return systemUrls;
  }
  return systemUrls.filter((p) => p.category === systemUrlFilter.value);
});

// Methods
async function loadTemplate() {
  if (isNewTemplate.value) {
    return;
  }

  isLoading.value = true;
  error.value = '';
  try {
    template.value = await emailService.getTemplate(templateId.value);

    // Populate form
    form.templateType = template.value.templateType;
    form.name = template.value.name;
    form.description = template.value.description;
    form.subject = template.value.subject;
    form.htmlBody = template.value.htmlBody;
    form.textBody = template.value.textBody || '';
    form.variables = JSON.parse(JSON.stringify(template.value.variables)); // Deep copy
    form.isActive = template.value.isActive;

    // Initialize shared variable values with example values
    template.value.variables.forEach((v) => {
      if (v.exampleValue) {
        variableValues[v.name] = v.exampleValue;
      }
    });

    // Prepopulate recipient email with logged-in user's email
    testEmail.recipientEmail = sessionStore.session.user?.email || '';

    // Store original form state for dirty checking
    originalFormState.value = JSON.stringify(form);

    // Track the email template view
    trackView({
      id: template.value.id,
      name: template.value.name,
      type: 'email-template',
      route: `/admin/email-templates/${template.value.id}`,
      description: template.value.description,
      icon: 'bi-envelope',
      metadata: {
        templateType: template.value.templateType,
        isActive: template.value.isActive,
        variableCount: template.value.variables.length,
      },
    });
  } catch (err: any) {
    error.value = err.message || 'Failed to load template';
    console.error('Error loading template:', err);
  } finally {
    isLoading.value = false;
  }
}

async function saveTemplate() {
  isSaving.value = true;
  saveResult.value = null;
  try {
    if (isNewTemplate.value) {
      // Create new template
      if (form.templateType === '') {
        saveResult.value = {
          success: false,
          message: 'Please enter a template type',
        };
        return;
      }

      const request: CreateEmailTemplateRequest = {
        templateType: form.templateType,
        name: form.name,
        description: form.description,
        subject: form.subject,
        htmlBody: form.htmlBody,
        textBody: form.textBody || undefined,
        variables: form.variables,
      };

      const created = await emailService.createTemplate(request);

      // Navigate to the new template's edit page
      router.push(`/admin/email-templates/${created.id}`);
    } else {
      // Update existing template
      const request: UpdateEmailTemplateRequest = {
        name: form.name,
        description: form.description,
        subject: form.subject,
        htmlBody: form.htmlBody,
        textBody: form.textBody || undefined,
        variables: form.variables,
        isActive: form.isActive,
      };

      template.value = await emailService.updateTemplate(templateId.value, request);

      // Reset dirty state after successful save
      originalFormState.value = JSON.stringify(form);

      saveResult.value = {
        success: true,
        message: 'Template saved successfully!',
      };

      // Scroll to top so user can see the result
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (err: any) {
    saveResult.value = {
      success: false,
      message: err.message || 'Failed to save template',
    };
    console.error('Error saving template:', err);

    // Scroll to top so user can see the error
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    isSaving.value = false;
  }
}

function addVariable() {
  form.variables.push({
    name: '',
    description: '',
    required: false,
    exampleValue: '',
  });
}

function removeVariable(index: number) {
  form.variables.splice(index, 1);
}

async function loadPreview() {
  if (!template.value) return;

  isLoadingPreview.value = true;
  try {
    preview.value = await emailService.previewTemplate(template.value.id, variableValues);
  } catch (err: any) {
    alert(`Failed to generate preview: ${err.message}`);
    console.error('Error loading preview:', err);
  } finally {
    isLoadingPreview.value = false;
  }
}

async function sendTest() {
  if (!template.value) return;

  isSendingTest.value = true;
  testResult.value = null;
  try {
    await emailService.sendTestEmail(template.value.id, {
      recipientEmail: testEmail.recipientEmail,
      testVariables: variableValues,
    });

    testResult.value = {
      success: true,
      message: `Test email sent successfully to ${testEmail.recipientEmail}`,
    };

    // Scroll to top so user can see the result
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (err: any) {
    testResult.value = {
      success: false,
      message: err.message || 'Failed to send test email',
    };
    console.error('Error sending test email:', err);

    // Scroll to top so user can see the error
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    isSendingTest.value = false;
  }
}

function handleTabChange(newTab: 'edit' | 'preview' | 'test') {
  // If switching away from edit tab and there are unsaved changes, warn user
  if (activeTab.value === 'edit' && newTab !== 'edit' && isDirty.value) {
    const confirmSwitch = confirm(
      'You have unsaved changes. If you switch tabs now, your changes will not be reflected in the preview or test.\n\n' +
      'Do you want to continue without saving?'
    );

    if (!confirmSwitch) {
      return; // Stay on current tab
    }
  }

  activeTab.value = newTab;
}

function goBack() {
  // Check for unsaved changes before navigating away
  if (isDirty.value) {
    const confirmLeave = confirm(
      'You have unsaved changes that will be lost.\n\n' +
      'Do you want to leave without saving?'
    );

    if (!confirmLeave) {
      return; // Stay on page
    }
  }

  router.push('/admin/email-templates');
}

function insertSystemUrl(systemUrl: SystemUrlInfo, targetField: 'html' | 'text' | 'subject') {
  const variableSyntax = `{{ ${systemUrl.templateVariable} }}`;

  if (targetField === 'subject') {
    // For subject, just append or insert at cursor if we can
    const currentSubject = form.subject;
    form.subject = currentSubject + variableSyntax;
  } else if (targetField === 'html') {
    const textarea = htmlBodyTextarea.value;
    if (textarea) {
      insertAtCursor(textarea, variableSyntax);
      form.htmlBody = textarea.value;
    } else {
      form.htmlBody += variableSyntax;
    }
  } else if (targetField === 'text') {
    const textarea = textBodyTextarea.value;
    if (textarea) {
      insertAtCursor(textarea, variableSyntax);
      form.textBody = textarea.value;
    } else {
      form.textBody += variableSyntax;
    }
  }

  showSystemUrlModal.value = false;
}

function insertAtCursor(textarea: HTMLTextAreaElement, text: string) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;

  textarea.value = value.substring(0, start) + text + value.substring(end);

  // Set cursor position after inserted text
  const newPosition = start + text.length;
  textarea.setSelectionRange(newPosition, newPosition);
  textarea.focus();
}

onMounted(async () => {
  await loadTemplate();
});
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        :title="isNewTemplate ? 'Create Email Template' : (template?.name || 'Loading...')"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Email Templates', href: '/admin/email-templates' },
          { label: isNewTemplate ? 'New' : (template?.name || '...') },
        ]"
      >
        <template #actions>
          <button class="bb-btn-secondary-cta" @click="goBack">
            <i class="bi bi-arrow-left me-2"></i>Back
          </button>
        </template>
      </PageHeader>

      <!-- Save Result Alert -->
      <div v-if="saveResult" class="mb-3">
        <div v-if="saveResult.success" class="alert alert-success alert-dismissible fade show" role="alert">
          <i class="bi bi-check-circle-fill me-2"></i>
          <strong>Success!</strong> {{ saveResult.message }}
          <button type="button" class="btn-close" @click="saveResult = null" aria-label="Close"></button>
        </div>
        <div v-else class="alert alert-danger alert-dismissible fade show" role="alert">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          <strong>Error:</strong> {{ saveResult.message }}
          <button type="button" class="btn-close" @click="saveResult = null" aria-label="Close"></button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ error }}
      </div>

      <!-- Tabs and Content -->
      <div v-else>
        <ul class="nav nav-tabs mb-4" role="tablist">
          <li class="nav-item" role="presentation">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'edit' }"
              @click="handleTabChange('edit')"
              type="button"
            >
              <i class="bi bi-pencil me-2"></i>Edit
              <span v-if="isDirty" class="badge bg-warning ms-1">*</span>
            </button>
          </li>
          <li v-if="!isNewTemplate" class="nav-item" role="presentation">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'preview' }"
              @click="handleTabChange('preview')"
              type="button"
            >
              <i class="bi bi-eye me-2"></i>Preview
            </button>
          </li>
          <li v-if="!isNewTemplate" class="nav-item" role="presentation">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'test' }"
              @click="handleTabChange('test')"
              type="button"
            >
              <i class="bi bi-send me-2"></i>Send Test
            </button>
          </li>
        </ul>

        <!-- Edit Tab -->
        <div v-if="activeTab === 'edit'">
          <form @submit.prevent="saveTemplate">
            <!-- Template Type -->
            <div class="row mb-3">
              <label for="template-type" class="col-sm-3 col-form-label">
                Template Type <span class="text-danger">*</span>
              </label>
              <div class="col-sm-9">
                <input
                  id="template-type"
                  v-model="form.templateType"
                  type="text"
                  class="form-control"
                  required
                  :disabled="!isNewTemplate"
                  placeholder="e.g., PasswordReset, OrgInvite, CustomNotification"
                  maxlength="100"
                  pattern="[A-Za-z0-9_]+"
                  title="Only letters, numbers, and underscores allowed"
                />
                <div class="form-text">
                  <span v-if="!isNewTemplate">Template type cannot be changed after creation</span>
                  <span v-else>Unique identifier for this template (letters, numbers, underscores only). Example: "PasswordReset"</span>
                </div>
              </div>
            </div>

            <!-- Template Name -->
            <div class="row mb-3">
              <label for="template-name" class="col-sm-3 col-form-label">
                Template Name <span class="text-danger">*</span>
              </label>
              <div class="col-sm-9">
                <input
                  id="template-name"
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  required
                  placeholder="e.g., Welcome Email"
                />
              </div>
            </div>

            <!-- Description -->
            <div class="row mb-3">
              <label for="template-description" class="col-sm-3 col-form-label">
                Description <span class="text-danger">*</span>
              </label>
              <div class="col-sm-9">
                <textarea
                  id="template-description"
                  v-model="form.description"
                  class="form-control"
                  rows="2"
                  required
                  placeholder="Brief description of this template's purpose"
                ></textarea>
              </div>
            </div>

            <!-- Email Subject -->
            <div class="row mb-3">
              <label for="template-subject" class="col-sm-3 col-form-label">
                Email Subject <span class="text-danger">*</span>
              </label>
              <div class="col-sm-9">
                <input
                  id="template-subject"
                  v-model="form.subject"
                  type="text"
                  class="form-control"
                  required
                  placeholder="e.g., Welcome to BlackBox!"
                />
                <div class="d-flex justify-content-between align-items-center mt-1">
                  <div class="form-text">
                    You can use variables like <code v-text="'{{userName}}'"></code>
                  </div>
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm"
                    @click="showSystemUrlModal = true"
                  >
                    <i class="bi bi-link-45deg me-1"></i>Insert System URL
                  </button>
                </div>
              </div>
            </div>

            <!-- HTML Body -->
            <div class="row mb-3">
              <label for="template-html" class="col-sm-3 col-form-label">
                HTML Body <span class="text-danger">*</span>
              </label>
              <div class="col-sm-9">
                <div class="d-flex justify-content-end mb-1">
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm"
                    @click="showSystemUrlModal = true"
                  >
                    <i class="bi bi-link-45deg me-1"></i>Insert System URL
                  </button>
                </div>
                <textarea
                  id="template-html"
                  ref="htmlBodyTextarea"
                  v-model="form.htmlBody"
                  class="form-control font-monospace"
                  rows="12"
                  required
                  placeholder="<html>..."
                ></textarea>
                <div class="form-text">
                  Use Liquid template syntax for variables: <code v-text="'{{variableName}}'"></code>
                </div>
              </div>
            </div>

            <!-- Plain Text Body -->
            <div class="row mb-3">
              <label for="template-text" class="col-sm-3 col-form-label">
                Plain Text Body <span class="text-muted">(Optional)</span>
              </label>
              <div class="col-sm-9">
                <div class="d-flex justify-content-end mb-1">
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm"
                    @click="showSystemUrlModal = true"
                  >
                    <i class="bi bi-link-45deg me-1"></i>Insert System URL
                  </button>
                </div>
                <textarea
                  id="template-text"
                  ref="textBodyTextarea"
                  v-model="form.textBody"
                  class="form-control font-monospace"
                  rows="8"
                  placeholder="Plain text version of the email..."
                ></textarea>
                <div class="form-text">Fallback for email clients that don't support HTML</div>
              </div>
            </div>

            <!-- Template Variables -->
            <div class="row mb-3">
              <div class="col-sm-3 col-form-label">
                <label>Template Variables</label>
              </div>
              <div class="col-sm-9">
                <div class="d-flex justify-content-end mb-2">
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm"
                    @click="addVariable"
                  >
                    <i class="bi bi-plus-lg me-1"></i>Add Variable
                  </button>
                </div>

                <div v-if="form.variables.length === 0" class="alert alert-info">
                  <i class="bi bi-info-circle me-2"></i>
                  No variables defined. Variables allow dynamic content in your emails.
                </div>

                <div v-else class="table-responsive">
                  <table class="table table-bordered">
                    <thead>
                      <tr>
                        <th style="width: 25%">Name</th>
                        <th style="width: 35%">Description</th>
                        <th style="width: 25%">Example Value</th>
                        <th style="width: 10%">Required</th>
                        <th style="width: 5%"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(variable, index) in form.variables" :key="index">
                        <td>
                          <input
                            v-model="variable.name"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="e.g., userName"
                            required
                          />
                        </td>
                        <td>
                          <input
                            v-model="variable.description"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="e.g., The user's display name"
                            required
                          />
                        </td>
                        <td>
                          <input
                            v-model="variable.exampleValue"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="e.g., John Smith"
                          />
                        </td>
                        <td>
                          <div class="form-check">
                            <input
                              v-model="variable.required"
                              type="checkbox"
                              class="form-check-input"
                              :id="`var-required-${index}`"
                            />
                            <label class="form-check-label" :for="`var-required-${index}`">
                              Required
                            </label>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            class="bb-btn-danger btn-sm text-nowrap"
                            @click="removeVariable(index)"
                          >
                            <i class="bi bi-trash me-1"></i>Remove
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Status -->
            <div v-if="!isNewTemplate" class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <div class="form-check form-switch">
                  <input
                    v-model="form.isActive"
                    type="checkbox"
                    class="form-check-input"
                    id="template-active"
                  />
                  <label class="form-check-label" for="template-active">
                    <strong>Active</strong>
                    <small class="text-muted d-block">
                      Only active templates can be used to send emails
                    </small>
                  </label>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="row">
              <div class="col-sm-9 offset-sm-3">
                <div class="d-flex justify-content-end gap-2">
                  <button type="submit" class="bb-btn-primary-cta" :disabled="isSaving">
                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="bi bi-save me-2"></i>
                    {{ isSaving ? 'Saving...' : isNewTemplate ? 'Create Template' : 'Save Changes' }}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Preview Tab -->
        <div v-if="activeTab === 'preview' && !isNewTemplate">
          <div class="row g-4">
            <!-- Sample Data Input -->
            <div class="col-md-4">
              <div class="card">
                <div class="card-body">
                  <h2 class="mb-3">Sample Data</h2>
                  <p class="text-muted small">
                    Provide sample values for template variables to preview the rendered email.
                  </p>

                  <div v-if="template?.variables && template.variables.length > 0">
                    <div
                      v-for="variable in template.variables"
                      :key="variable.name"
                      class="mb-3"
                    >
                      <label :for="`sample-${variable.name}`" class="form-label">
                        {{ variable.name }}
                        <span v-if="variable.required" class="text-danger">*</span>
                      </label>
                      <input
                        :id="`sample-${variable.name}`"
                        v-model="variableValues[variable.name]"
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="variable.exampleValue || variable.description"
                        :required="variable.required"
                      />
                      <small class="text-muted">{{ variable.description }}</small>
                    </div>

                    <button
                      class="bb-btn-primary-cta w-100"
                      @click="loadPreview"
                      :disabled="isLoadingPreview"
                    >
                      <span v-if="isLoadingPreview" class="spinner-border spinner-border-sm me-2"></span>
                      <i v-else class="bi bi-eye me-2"></i>
                      {{ isLoadingPreview ? 'Loading...' : 'Generate Preview' }}
                    </button>
                  </div>
                  <div v-else class="alert alert-info mb-0">
                    <i class="bi bi-info-circle me-2"></i>
                    No variables defined for this template.
                  </div>
                </div>
              </div>
            </div>

            <!-- Preview Display -->
            <div class="col-md-8">
              <div class="card">
                <div class="card-body">
                  <h2 class="mb-3">Email Preview</h2>

                  <div v-if="!preview" class="text-center py-5 text-muted">
                    <i class="bi bi-eye-slash display-3 mb-3 d-block"></i>
                    <p>Click "Generate Preview" to see the rendered email</p>
                  </div>

                  <div v-else>
                    <!-- Preview Tabs -->
                    <ul class="nav nav-pills mb-3">
                      <li class="nav-item">
                        <a
                          class="nav-link"
                          :class="{ active: previewMode === 'rendered' }"
                          @click="previewMode = 'rendered'"
                          href="javascript:void(0)"
                        >
                          Rendered
                        </a>
                      </li>
                      <li class="nav-item">
                        <a
                          class="nav-link"
                          :class="{ active: previewMode === 'html' }"
                          @click="previewMode = 'html'"
                          href="javascript:void(0)"
                        >
                          HTML
                        </a>
                      </li>
                      <li v-if="preview.textBody" class="nav-item">
                        <a
                          class="nav-link"
                          :class="{ active: previewMode === 'text' }"
                          @click="previewMode = 'text'"
                          href="javascript:void(0)"
                        >
                          Text
                        </a>
                      </li>
                    </ul>

                    <!-- Subject -->
                    <div class="mb-3">
                      <strong>Subject:</strong>
                      <div class="form-control-plaintext">{{ preview.subject }}</div>
                    </div>

                    <!-- Preview Content -->
                    <div v-if="previewMode === 'rendered'" class="preview-rendered border rounded p-3">
                      <iframe
                        ref="previewFrame"
                        :srcdoc="preview.htmlBody"
                        class="w-100"
                        style="min-height: 500px; border: none;"
                        sandbox="allow-same-origin"
                      ></iframe>
                    </div>

                    <div v-else-if="previewMode === 'html'" class="preview-source">
                      <pre class="border rounded p-3 bg-body-tertiary"><code>{{ preview.htmlBody }}</code></pre>
                    </div>

                    <div v-else-if="previewMode === 'text'" class="preview-source">
                      <pre class="border rounded p-3 bg-body-tertiary">{{ preview.textBody }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Send Test Tab -->
        <div v-if="activeTab === 'test' && !isNewTemplate">
          <div class="row g-4">
            <!-- Test Email Form -->
            <div class="col-md-6">
              <div class="card">
                <div class="card-body">
                  <h2 class="mb-3">Send Test Email</h2>
                  <p class="text-muted">
                    Send a test email to verify the template rendering and delivery.
                  </p>

                  <form @submit.prevent="sendTest">
                    <div class="mb-3">
                      <label for="test-recipient" class="form-label">
                        Recipient Email <span class="text-danger">*</span>
                      </label>
                      <input
                        id="test-recipient"
                        v-model="testEmail.recipientEmail"
                        type="email"
                        class="form-control"
                        required
                        placeholder="test@example.com"
                      />
                    </div>

                    <h3 class="mb-3">Test Variables</h3>
                    <div v-if="template?.variables && template.variables.length > 0">
                      <div
                        v-for="variable in template.variables"
                        :key="variable.name"
                        class="mb-3"
                      >
                        <label :for="`test-${variable.name}`" class="form-label">
                          {{ variable.name }}
                          <span v-if="variable.required" class="text-danger">*</span>
                        </label>
                        <input
                          :id="`test-${variable.name}`"
                          v-model="variableValues[variable.name]"
                          type="text"
                          class="form-control"
                          :placeholder="variable.exampleValue || variable.description"
                          :required="variable.required"
                        />
                        <small class="text-muted">{{ variable.description }}</small>
                      </div>
                    </div>
                    <div v-else class="alert alert-info">
                      <i class="bi bi-info-circle me-2"></i>
                      No variables required for this template.
                    </div>

                    <button
                      type="submit"
                      class="bb-btn-primary-cta w-100"
                      :disabled="isSendingTest"
                    >
                      <span v-if="isSendingTest" class="spinner-border spinner-border-sm me-2"></span>
                      <i v-else class="bi bi-send me-2"></i>
                      {{ isSendingTest ? 'Sending...' : 'Send Test Email' }}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <!-- Test Result -->
            <div class="col-md-6">
              <div class="card">
                <div class="card-body">
                  <h2 class="mb-3">Test Result</h2>

                  <div v-if="!testResult" class="text-center py-5 text-muted">
                    <i class="bi bi-send-slash display-3 mb-3 d-block"></i>
                    <p>Submit the form to send a test email</p>
                  </div>

                  <div v-else-if="testResult.success" class="alert alert-success">
                    <i class="bi bi-check-circle-fill me-2"></i>
                    <strong>Success!</strong>
                    <p class="mb-0 mt-2">{{ testResult.message }}</p>
                  </div>

                  <div v-else class="alert alert-danger">
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>Error</strong>
                    <p class="mb-0 mt-2">{{ testResult.message }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PanelCard>
  </PanelGrid>

  <!-- System URL Helper Modal -->
  <div
    v-if="showSystemUrlModal"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    @click.self="showSystemUrlModal = false"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-link-45deg me-2"></i>Insert System URL
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="showSystemUrlModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info mb-3">
            <i class="bi bi-info-circle me-2"></i>
            <strong>System-generated URLs:</strong> These URLs are automatically populated with the correct base URL (localhost/staging/production) and required parameters at email send time.
          </div>

          <!-- Category Filter -->
          <div class="mb-4">
            <label class="form-label">Filter by Category</label>
            <div class="btn-group d-flex" role="group">
              <input
                type="radio"
                class="btn-check"
                id="filter-all"
                value="All"
                v-model="systemUrlFilter"
              />
              <label class="btn btn-outline-primary" for="filter-all">All</label>

              <input
                type="radio"
                class="btn-check"
                id="filter-auth"
                value="Authentication"
                v-model="systemUrlFilter"
              />
              <label class="btn btn-outline-primary" for="filter-auth">Authentication</label>

              <input
                type="radio"
                class="btn-check"
                id="filter-invites"
                value="Invitations"
                v-model="systemUrlFilter"
              />
              <label class="btn btn-outline-primary" for="filter-invites">Invitations</label>

              <input
                type="radio"
                class="btn-check"
                id="filter-nav"
                value="Navigation"
                v-model="systemUrlFilter"
              />
              <label class="btn btn-outline-primary" for="filter-nav">Navigation</label>
            </div>
          </div>

          <!-- System URL List -->
          <div class="system-url-list">
            <div
              v-for="systemUrl in filteredSystemUrls"
              :key="systemUrl.templateVariable"
              class="system-url-card card mb-3"
            >
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h3 class="mb-0">{{ systemUrl.name }}</h3>
                  <span class="badge bg-secondary">{{ systemUrl.category }}</span>
                </div>

                <p class="text-muted small mb-3">{{ systemUrl.description }}</p>

                <div class="mb-2">
                  <strong class="small d-block mb-1">Template Variable:</strong>
                  <code class="d-block bg-body-tertiary p-2 rounded" v-text="`{{ ${systemUrl.templateVariable} }}`"></code>
                </div>

                <div v-if="systemUrl.requiredDataVariables.length > 0" class="mb-2">
                  <strong class="small d-block mb-1">Required Data Variables:</strong>
                  <div>
                    <span
                      v-for="varName in systemUrl.requiredDataVariables"
                      :key="varName"
                      class="badge bg-warning me-1"
                    >
                      {{ varName }}
                    </span>
                  </div>
                  <small class="text-muted d-block mt-1">
                    You must provide these variables when sending the email
                  </small>
                </div>

                <div v-if="systemUrl.pattern" class="mb-3">
                  <strong class="small d-block mb-1">URL Pattern:</strong>
                  <code class="d-block bg-body-tertiary p-2 rounded text-wrap small">{{ systemUrl.pattern }}</code>
                </div>

                <div class="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm"
                    @click="insertSystemUrl(systemUrl, 'subject')"
                  >
                    <i class="bi bi-envelope me-1"></i>Insert in Subject
                  </button>
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm"
                    @click="insertSystemUrl(systemUrl, 'text')"
                  >
                    <i class="bi bi-file-text me-1"></i>Insert in Text
                  </button>
                  <button
                    type="button"
                    class="bb-btn-primary-cta btn-sm"
                    @click="insertSystemUrl(systemUrl, 'html')"
                  >
                    <i class="bi bi-code-square me-1"></i>Insert in HTML
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="showSystemUrlModal = false"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showSystemUrlModal" class="modal-backdrop fade show" @click="showSystemUrlModal = false"></div>
</template>

<style scoped lang="scss">
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.table th {
  font-weight: 600;
  color: var(--bs-body-color);
  font-size: 0.875rem;
  border-bottom: 2px solid var(--bs-border-color);
}

.table td {
  vertical-align: middle;
  font-size: 0.875rem;
}

hr {
  margin: 1.5rem 0;
}

.preview-rendered iframe {
  background: white;
}

.preview-source pre {
  max-height: 600px;
  overflow: auto;
  font-size: 0.875rem;
}

.form-check-input {
  cursor: pointer;
}

/* System URL Modal */
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal.show {
  display: block;
}

.system-url-list {
  max-height: 500px;
  overflow-y: auto;
}

.system-url-card {
  transition: all 0.2s ease;
  border: 1px solid var(--bs-border-color);
}

.system-url-card:hover {
  border-color: var(--bs-primary);
  box-shadow: 0 0.125rem 0.5rem rgba(var(--bs-primary-rgb), 0.15);
}

.system-url-card code {
  font-size: 0.875rem;
  word-break: break-word;
}

.system-url-card .badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.35em 0.65em;
}
</style>
